import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@xemplo/auth-provider';
import { CommonRoutes, CommonSkipSessionStorageRoutes } from '@xemplo/common-routes';
import { isFramed } from '@xemplo/common-util';
import { safeCallAsync } from '@xemplo/safe-call';
import { tracker } from '@xemplo/tracking';
import { LoginActions } from '@xemplo/types';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { LoginProps } from '../auth-helper.types';
import {
  broadcastLogin,
  clearLogoutMarker,
  setActiveSub,
} from '../helpers/auth-broadcast.helper';

export function useLogin({ action }: LoginProps) {
  const { hasTokenHandover, contextReturnUrlKey } = useAppSettings();
  const { isLoading, isAuthenticated, signinRedirect, signinSilent, user } = useAuth();
  const navigate = useNavigate();
  const hasAttemptedSilentRef = useRef(false);

  // Framed, the host's src is the destination, not wherever this app last was.
  let sessionUrl = isFramed() ? null : sessionStorage.getItem(contextReturnUrlKey);
  if (sessionUrl && CommonSkipSessionStorageRoutes.includes(sessionUrl)) {
    sessionUrl = null;
  }

  const returnUrl = sessionUrl ?? CommonRoutes.Root;

  // Reach the app without an interactive redirect when an SSO session already
  // exists (e.g. crossing from hr-fe): try a silent sign-in first and only fall
  // back to the full redirect if it fails (no IdP session → genuine first login).
  const signInSilentlyThenRedirect = useCallback(async () => {
    const [renewedUser] = await safeCallAsync(signinSilent());
    if (!renewedUser) signinRedirect();
  }, [signinSilent, signinRedirect]);

  useEffect(() => {
    if (hasTokenHandover) {
      tracker.setUserId(user?.profile?.sub);
      navigate(returnUrl, { replace: true });
      return;
    }

    if (isLoading) return;

    if (isAuthenticated) {
      // A fresh sign-in supersedes any prior cross-app logout; drop the marker so
      // the durable logout backstop can't re-logout this new session (clock skew).
      clearLogoutMarker();
      // Publish the new identity so the other stack (and other tabs) can detect a
      // user switch and re-establish auth: the durable marker for later loads, the
      // broadcast for tabs open right now.
      const sub = user?.profile?.sub;
      if (sub) {
        setActiveSub(sub);
        broadcastLogin(sub);
      }
      tracker.setUserId(sub);
      navigate(returnUrl, { replace: true });
      return;
    }

    if (action === LoginActions.Login && !hasAttemptedSilentRef.current) {
      hasAttemptedSilentRef.current = true;
      signInSilentlyThenRedirect();
    }
  }, [
    action,
    hasTokenHandover,
    isAuthenticated,
    isLoading,
    returnUrl,
    navigate,
    signInSilentlyThenRedirect,
    user,
  ]);
}
